import React from 'react';

import { Icon, Stack, Text, tv } from '@eluve/components';

const STEPS = [
  { id: 'upload', label: 'Upload', num: 1 },
  { id: 'review', label: 'Review', num: 2 },
  { id: 'finalize', label: 'Finalize', num: 3 },
  { id: 'importing', label: 'Import', num: 4 },
] as const;

type FlowStep = (typeof STEPS)[number]['id'];

type StepState = 'done' | 'active' | 'pending';

const stepCircleStyles = tv({
  base: 'flex size-7 items-center justify-center rounded-full border text-xs font-semibold tabular-nums',
  variants: {
    state: {
      done: 'border-bg-accent-strong-secondary bg-bg-accent-strong-secondary text-text-accent-strong',
      active:
        'border-bg-accent-strong-secondary bg-bg-accent-strong-secondary text-text-accent-strong',
      pending: 'border-border-tertiary bg-bg-primary text-text-tertiary',
    },
  },
});

const stepConnectorStyles = tv({
  base: 'h-px flex-1',
  variants: {
    done: {
      true: 'bg-bg-accent-strong-secondary',
      false: 'bg-border-tertiary',
    },
  },
});

const stepStateFor = (i: number, curIdx: number): StepState => {
  if (i < curIdx) return 'done';
  if (i === curIdx) return 'active';
  return 'pending';
};

const StepCircle: React.FC<{ state: StepState; num: number }> = ({
  state,
  num,
}) => (
  <div className={stepCircleStyles({ state })}>
    {state === 'done' ? <Icon name="Check" size="xs" /> : num}
  </div>
);

export const FlowStepper: React.FC<{ current: FlowStep }> = ({ current }) => {
  const curIdx = STEPS.findIndex((s) => s.id === current);

  return (
    <Stack
      direction="horizontal"
      gap={2}
      align="center"
      className="w-full rounded-md bg-bg-secondary px-4 py-3"
    >
      {STEPS.map((s, i) => {
        const state = stepStateFor(i, curIdx);
        return (
          <React.Fragment key={s.id}>
            <Stack direction="horizontal" gap={2} align="center">
              <StepCircle state={state} num={s.num} />
              <Text
                size="sm"
                weight={state === 'active' ? 'semibold' : 'regular'}
                color={state === 'pending' ? 'tertiary' : 'primary'}
              >
                {s.label}
              </Text>
            </Stack>
            {i < STEPS.length - 1 ? (
              <div className={stepConnectorStyles({ done: i < curIdx })} />
            ) : null}
          </React.Fragment>
        );
      })}
    </Stack>
  );
};
